import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import { SessionScope } from '@heidi/schema/ml-scribe';
import { withoutPendingServerDeletes } from '@heidi/session/contracts';
import {
  flattenSessionListPages,
  useQuerySessionsList,
  type UseQuerySessionsListParams,
} from '@heidi/session/data';
import { useMemo } from 'react';

/**
 * Dedicated "most recent activity" list instance: own cache entry keyed by the
 * caller's `sortBy` (Recents uses `createdAt desc`), kept fresh by the
 * built-in 30s first-page poll. Only mounted while the nav-v3 tray is
 * expanded, so the extra poll is bounded.
 *
 * Deliberately NOT `useCachedSessionListItems` (a non-subscribing snapshot of
 * whatever sort the list page last used) and NOT `useQueryCombinedSessions`
 * (the legacy `/sessions` fetch).
 */
const RECENT_VISITS_PARAMS: UseQuerySessionsListParams = {
  scope: SessionScope.me,
  selectedStatuses: [],
  selectedDateRange: {},
  selectedPatientProfiles: [],
  selectedClinicianIds: [],
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  searchQuery: '',
  isUrgentOnly: false,
};

export interface UseRecentVisitsOptions {
  /** Server-backed encounter search term. Empty string browses unfiltered. */
  searchQuery?: string;
  /** Turns the query (and its first-page poll) off entirely — used to skip
   *  fetching encounters while a search is active under the Chats filter. */
  enabled?: boolean;
  /** Keeps the 30s first-page poll scoped to the persistent Recents tray.
   *  The primary-rail hover probe reads/fetches the same cache without
   *  starting its own permanent poll. */
  pollEnabled?: boolean;
}

export function useRecentVisits(
  sortBy: UseQuerySessionsListParams['sortBy'] = 'updatedAt',
  { searchQuery = '', enabled = true, pollEnabled = true }: UseRecentVisitsOptions = {}
) {
  const params = useMemo<UseQuerySessionsListParams>(
    () => ({ ...RECENT_VISITS_PARAMS, sortBy, searchQuery }),
    [sortBy, searchQuery]
  );
  const query = useQuerySessionsList(params, { pollEnabled, enabled });

  // This tray has its own cache entry and does NOT pass through
  // `useSessionListData`, so nothing upstream has filtered for it.
  const pendingServerDeleteIds = useOfflinePendingServerDeleteIds();
  const visits = useMemo(
    () =>
      withoutPendingServerDeletes(
        flattenSessionListPages(query.data?.pages),
        pendingServerDeleteIds
      ),
    [query.data?.pages, pendingServerDeleteIds]
  );

  return {
    visits,
    hasResolvedData: query.data !== undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetching: query.isFetching,
    // `keepPreviousData` shows the prior query's rows as a stand-in while a
    // new `searchQuery` fetches — real for browsing (no flicker moving
    // between loaded pages), but a caller matching search results against
    // the active query must treat this as "not settled yet" so a stale
    // (wrong-query) row is never presented as a match.
    isPlaceholderData: query.isPlaceholderData,
  };
}
